import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Chip, Alert, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Divider, useTheme,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import LayersIcon from '@mui/icons-material/Layers';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloseIcon from '@mui/icons-material/Close';
import MapView from './MapView';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { readFileAsText, parseAnyGeoFile, exportGeoJSON, haversineDistance } from '../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';

const COLORS = ['#6366f1', '#f43f5e', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];

function GeoJsonViewer() {
  const theme = useTheme();
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [rawText, setRawText] = useState('');
  const [error, setError] = useState('');
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showPasteArea, setShowPasteArea] = useState(false);
  const [formatInfo, setFormatInfo] = useState(null);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const result = parseAnyGeoFile(text);
      if (result.error || !result.geoJson) throw new Error(result.error || 'Could not parse file');
      setGeoJsonData(result.geoJson);
      setRawText(JSON.stringify(result.geoJson, null, 2));
      setFormatInfo(result);
      setError('');
    } catch (err) {
      setError(`Failed to parse file: ${err.message}`);
    }
  }, []);

  const handlePaste = useCallback(() => {
    try {
      const result = parseAnyGeoFile(rawText);
      if (result.error || !result.geoJson) throw new Error(result.error || 'Could not parse input');
      setGeoJsonData(result.geoJson);
      setFormatInfo(result);
      setError('');
      setShowPasteArea(false);
    } catch (err) {
      setError(`Parse error: ${err.message}`);
    }
  }, [rawText]);

  const stats = useMemo(() => {
    if (!geoJsonData) return null;
    const features = geoJsonData.features || [];
    const typeBreakdown = {};
    let totalCoords = 0;

    features.forEach(f => {
      const gType = f.geometry?.type || 'Unknown';
      typeBreakdown[gType] = (typeBreakdown[gType] || 0) + 1;

      const countCoords = (coords) => {
        if (!coords) return 0;
        if (typeof coords[0] === 'number') return 1;
        return coords.reduce((s, c) => s + countCoords(c), 0);
      };
      totalCoords += countCoords(f.geometry?.coordinates);
    });

    // Calculate bounding box
    let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
    const extractCoords = (coords) => {
      if (!coords) return;
      if (typeof coords[0] === 'number' && coords.length >= 2) {
        minLon = Math.min(minLon, coords[0]);
        maxLon = Math.max(maxLon, coords[0]);
        minLat = Math.min(minLat, coords[1]);
        maxLat = Math.max(maxLat, coords[1]);
        return;
      }
      coords.forEach(c => extractCoords(c));
    };
    features.forEach(f => extractCoords(f.geometry?.coordinates));

    // Total distance for LineStrings
    let totalDistanceM = 0;
    features.forEach(f => {
      if (f.geometry?.type === 'LineString') {
        const c = f.geometry.coordinates;
        for (let i = 1; i < c.length; i++) {
          totalDistanceM += haversineDistance(c[i - 1], c[i]);
        }
      }
    });

    return {
      featureCount: features.length,
      typeBreakdown,
      totalCoords,
      bbox: features.length > 0 ? { minLat, maxLat, minLon, maxLon } : null,
      totalDistanceKm: totalDistanceM / 1000,
    };
  }, [geoJsonData]);

  const styleFunction = useCallback((feature) => {
    const types = Object.keys(stats?.typeBreakdown || {});
    const idx = types.indexOf(feature.geometry?.type) % COLORS.length;
    return {
      color: COLORS[idx >= 0 ? idx : 0],
      weight: 3,
      opacity: 0.85,
      fillOpacity: 0.2,
    };
  }, [stats]);

  const handleFeatureClick = useCallback((feature) => {
    setSelectedFeature(feature);
  }, []);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="GeoJSON / GPX / KML Viewer | Interactive Map Editor | UmbrellaCo"
        description="View, inspect, and analyze GeoJSON, GPX, KML, JSON, CSV, TSV, and coordinate data on an interactive map. Automatically parses formats and visualizes routes, polygons, and points."
        keywords="geojson viewer, map viewer, view geojson online, inspect spatial data, csv to map"
        url="/spatial-tools/geojson-viewer"
        jsonLd={createSoftwareAppSchema(
          'GeoJSON Viewer',
          'View, inspect, and analyze GeoJSON, CSV, and coordinate data on an interactive map.',
          'https://umbrellaco.in/spatial-tools/geojson-viewer',
          ['Spatial Data Visualization', 'GeoJSON Inspector', 'Interactive Maps']
        )}
      />
      <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>GeoJSON / GPX / KML Viewer</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload or paste spatial data (GeoJSON, GPX, KML, CSV, WKT, plain text) to instantly visualize and inspect it on an interactive map.
        </Typography>
      </Box>
      <PanelGroup direction="horizontal">
        {/* Left Panel - Controls & Info */}
        <Panel defaultSize={35} minSize={22}>
          <Box sx={{
            height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}`,
          }}>
            {/* Upload Section */}
            <Box sx={{ p: 2, flexShrink: 0 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  size="small"
                  sx={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
                  }}
                >
                  Upload File
                  <input type="file" hidden accept=".geojson,.json,.csv,.tsv,.txt" onChange={handleFileUpload} />
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ContentPasteIcon />}
                  size="small"
                  onClick={() => setShowPasteArea(!showPasteArea)}
                >
                  Paste
                </Button>
              </Stack>

              {showPasteArea && (
                <Box sx={{ mb: 1.5 }}>
                  <TextField
                    multiline rows={6} fullWidth size="small"
                    placeholder="Paste GeoJSON, CSV, or coordinates here..."
                    value={rawText}
                    onChange={(e) => setRawText(e.target.value)}
                    sx={{ fontFamily: 'monospace', fontSize: '0.8rem', mb: 1 }}
                  />
                  <Button variant="contained" size="small" onClick={handlePaste} fullWidth>
                    Load Data
                  </Button>
                  {formatInfo && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip size="small" label={`Format: ${formatInfo.format}`} variant="outlined" />
                      {formatInfo.meta?.detectedSeparator && (
                        <Chip size="small" label={`Sep: ${formatInfo.meta.detectedSeparator}`} variant="outlined" />
                      )}
                    </Stack>
                  )}
                </Box>
              )}

              {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            </Box>

            <Divider />

            {/* Statistics */}
            {stats && (
              <Box sx={{ p: 2, flexShrink: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                  <BarChartIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={700}>Statistics</Typography>
                </Stack>
                <Box sx={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1,
                }}>
                  {[
                    { label: 'Features', value: stats.featureCount },
                    { label: 'Coordinates', value: stats.totalCoords.toLocaleString() },
                    { label: 'Distance', value: stats.totalDistanceKm > 0 ? `${stats.totalDistanceKm.toFixed(2)} km` : '—' },
                    { label: 'Types', value: Object.keys(stats.typeBreakdown).length },
                  ].map(({ label, value }) => (
                    <Paper key={label} variant="outlined" sx={{
                      p: 1, textAlign: 'center',
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.08) 100%)'
                        : 'linear-gradient(135deg, rgba(99,102,241,0.04) 0%, rgba(139,92,246,0.04) 100%)',
                    }}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={700}>{value}</Typography>
                    </Paper>
                  ))}
                </Box>

                <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.entries(stats.typeBreakdown).map(([type, count], idx) => (
                    <Chip
                      key={type}
                      label={`${type}: ${count}`}
                      size="small"
                      sx={{
                        bgcolor: COLORS[idx % COLORS.length] + '22',
                        color: COLORS[idx % COLORS.length],
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  ))}
                </Stack>

                {stats.bbox && (
                  <Paper variant="outlined" sx={{ p: 1, mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Bounding Box</Typography>
                    <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', mt: 0.5 }}>
                      SW: [{stats.bbox.minLon.toFixed(4)}, {stats.bbox.minLat.toFixed(4)}]
                      <br />
                      NE: [{stats.bbox.maxLon.toFixed(4)}, {stats.bbox.maxLat.toFixed(4)}]
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            <Divider />

            {/* Feature Table */}
            {geoJsonData && geoJsonData.features.length > 0 && (
              <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                  <LayersIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={700}>Features</Typography>
                </Stack>
                <TableContainer sx={{ flexGrow: 1, overflowY: 'auto', px: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5 }}>Props</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {geoJsonData.features.map((f, i) => (
                        <TableRow
                          key={i}
                          hover
                          selected={selectedFeature === f}
                          onClick={() => setSelectedFeature(f)}
                          sx={{ cursor: 'pointer', '& td': { py: 0.5, fontSize: '0.75rem' } }}
                        >
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>
                            <Chip
                              label={f.geometry?.type || '?'}
                              size="small"
                              sx={{
                                height: 20, fontSize: '0.65rem', fontWeight: 600,
                                bgcolor: COLORS[Object.keys(stats?.typeBreakdown || {}).indexOf(f.geometry?.type) % COLORS.length] + '22',
                                color: COLORS[Object.keys(stats?.typeBreakdown || {}).indexOf(f.geometry?.type) % COLORS.length],
                              }}
                            />
                          </TableCell>
                          <TableCell>{Object.keys(f.properties || {}).length}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Selected Feature Properties */}
            {selectedFeature && (
              <Box sx={{
                p: 2, borderTop: `1px solid ${theme.palette.divider}`,
                maxHeight: '30%', overflowY: 'auto', flexShrink: 0,
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <InfoIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight={700}>Properties</Typography>
                  </Stack>
                  <IconButton size="small" onClick={() => setSelectedFeature(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Box sx={{
                  fontFamily: 'monospace', fontSize: '0.75rem',
                  bgcolor: 'action.hover', borderRadius: 1, p: 1,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {JSON.stringify(selectedFeature.properties, null, 2)}
                </Box>
              </Box>
            )}

            {/* Export */}
            {geoJsonData && (
              <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => exportGeoJSON(geoJsonData, 'geojson', 'export')}>
                    .geojson
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => exportGeoJSON(geoJsonData, 'kml', 'export')}>
                    .kml
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => exportGeoJSON(geoJsonData, 'gpx', 'export')}>
                    .gpx
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        </Panel>

        <PanelResizeHandle style={{ width: '5px', background: theme.palette.divider, cursor: 'col-resize' }} />

        {/* Right Panel - Map */}
        <Panel defaultSize={65} minSize={40}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {geoJsonData ? (
              <MapView
                mapId="geojson-viewer-map"
                geoJsonData={geoJsonData}
                styleFunction={styleFunction}
                onFeatureClick={handleFeatureClick}
              />
            ) : (
              <Box sx={{
                height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
              }}>
                <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center', p: 4 }}>
                  <UploadFileIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography variant="h6" color="text.secondary">Upload or paste a GeoJSON file</Typography>
                  <Typography variant="body2" color="text.disabled">
                    Supports GeoJSON, CSV, TSV, and plain coordinates
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}

export default GeoJsonViewer;
